/* Birthdays (owner request 2026-11-01), a Little surprise, the pure part: what a person's list looks like, which
   birthdays fall on a date, merging two copies of the list (this device's and the database's), the messages. No screen,
   no storage: the device copy and sending are in src/birthdays.ts, the celebration in src/bdayfx.ts.
   The list is personal: the database lets each person read and write only their own (migration 017).
   The year of the person's own birthday is optional and never shown; no age is ever worked out from it.
   Feb 29 birthdays are celebrated on Feb 28 in years without a Feb 29. Never names the old product. */

/** the person's own birthday ({m: null, d: null} = cleared); at = when it was last set (ISO) */
export interface Own { m: number | null; d: number | null; y: number | null; at: string }
/** someone in "Family & friends"; del = removed (kept a while so the person's other devices learn of it) */
export interface Friend { id: string; name: string; m: number; d: number; at: string; del?: true }
export interface Birthdays { own: Own | null; people: Friend[] }

export const MAX_PEOPLE = 100, NAME_MAX = 40, ROWS_MAX = 300;
/** removed rows are forgotten after this long (every device has heard of the removal by then) */
export const TOMBSTONE_DAYS = 120;
export const empty = (): Birthdays => ({own: null, people: []});
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON3 = MONTHS.map(m => m.slice(0, 3));

const leap = (y: number) => y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
const daysIn = (m: number) => m === 2 ? 29 : [4, 6, 9, 11].includes(m) ? 30 : 31;
/** a real month and day (Feb 29 allowed) */
export const validMD = (m: unknown, d: unknown): boolean => Number.isInteger(m) && Number.isInteger(d) && (m as number) >= 1 && (m as number) <= 12 && (d as number) >= 1 && (d as number) <= daysIn(m as number);
/** a year for the person's own birthday: empty, or 1900 to this year (and Feb 29 only in a leap year) */
export function validYear(y: unknown, m: number, d: number, thisYear = new Date().getFullYear()): boolean {
  if (y === null || y === undefined) return true;
  return Number.isInteger(y) && (y as number) >= 1900 && (y as number) <= thisYear && !(m === 2 && d === 29 && !leap(y as number));
}
/** a name as stored: trimmed, inner spaces kept; '' when not usable (empty, too long, control characters) */
export function cleanName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const n = raw.trim();
  return n.length >= 1 && [...n].length <= NAME_MAX && !/[\u0000-\u001f\u007f-\u009f]/.test(n) ? n : '';
}
const ID = /^[A-Za-z0-9_-]{1,40}$/;
const isoOk = (s: unknown): s is string => typeof s === 'string' && !isNaN(Date.parse(s));

/** what a stored or received value says (anything odd is left out; never throws) */
export function parse(raw: unknown): Birthdays {
  let v: any = raw;
  if (typeof raw === 'string') { try { v = JSON.parse(raw); } catch { return empty(); } }
  if (!v || typeof v !== 'object') return empty();
  let own: Own | null = null;
  const o = v.own;
  if (o && typeof o === 'object' && isoOk(o.at)) {
    if (o.m === null && o.d === null) own = {m: null, d: null, y: null, at: o.at};
    else if (validMD(o.m, o.d)) own = {m: o.m, d: o.d, y: validYear(o.y, o.m, o.d, 9999) && o.y != null ? o.y : null, at: o.at};
  }
  const seen = new Set<string>(), people: Friend[] = [];
  for (const p of Array.isArray(v.people) ? v.people : []) {
    if (!p || typeof p !== 'object' || typeof p.id !== 'string' || !ID.test(p.id) || seen.has(p.id) || !isoOk(p.at)) continue;
    if (p.del === true) { seen.add(p.id); people.push({id: p.id, name: '', m: 1, d: 1, at: p.at, del: true}); continue; }
    const name = cleanName(p.name);
    if (!name || !validMD(p.m, p.d)) continue;
    seen.add(p.id); people.push({id: p.id, name, m: p.m, d: p.d, at: p.at});
  }
  return {own, people};
}
/** what is sent to the database (removed rows without their old name and date) */
export function toJson(b: Birthdays): {own: Own | null; people: object[]} {
  return {own: b.own ? {m: b.own.m, d: b.own.d, y: b.own.y, at: b.own.at} : null,
    people: b.people.map(p => p.del ? {id: p.id, del: true, at: p.at} : {id: p.id, name: p.name, m: p.m, d: p.d, at: p.at})};
}
/** the same list? (order of people and removed rows included) */
export const same = (a: Birthdays, b: Birthdays) => JSON.stringify(toJson(a)) === JSON.stringify(toJson(b));
export const live = (b: Birthdays) => b.people.filter(p => !p.del);

/** two copies of the list put together: the own birthday and each person by its latest change (a removal is a change
    too); people only one copy has are kept. Removed rows older than TOMBSTONE_DAYS are forgotten. Order: the first
    copy's, then new ones from the second. Nothing is lost: an edit made offline on one device and another made on a
    second device both survive (unless they changed the very same row, then the later one wins). */
export function merge(a: Birthdays, b: Birthdays, now = Date.now()): Birthdays {
  const later = <T extends {at: string}>(x: T | null, y: T | null) => !x ? y : !y ? x : Date.parse(y.at) > Date.parse(x.at) ? y : x;
  const own = later(a.own, b.own);
  const byId = new Map<string, Friend>();
  for (const p of a.people) byId.set(p.id, p);
  for (const p of b.people) byId.set(p.id, later(byId.get(p.id) || null, p)!);
  const order = [...a.people.map(p => p.id), ...b.people.map(p => p.id).filter(id => !a.people.some(p => p.id === id))];
  const cut = now - TOMBSTONE_DAYS * 86400000;
  let people = order.map(id => byId.get(id)!).filter(p => !p.del || Date.parse(p.at) >= cut);
  if (people.length > ROWS_MAX) {   // (only removed rows can make it too long: the oldest removals go first)
    const drop = new Set(people.filter(p => p.del).sort((x, y) => Date.parse(x.at) - Date.parse(y.at)).slice(0, people.length - ROWS_MAX).map(p => p.id));
    people = people.filter(p => !drop.has(p.id));
  }
  return {own, people};
}

/* ---------- edits (each one stamped with the moment it was made) ---------- */
export function setOwn(b: Birthdays, m: number | null, d: number | null, y: number | null, at = new Date().toISOString()): Birthdays {
  return {...b, own: m === null || d === null ? {m: null, d: null, y: null, at} : {m, d, y, at}};
}
export function addFriend(b: Birthdays, id: string, name: string, m: number, d: number, at = new Date().toISOString()): Birthdays {
  return {...b, people: [...b.people, {id, name: cleanName(name), m, d, at}]};
}
export function editFriend(b: Birthdays, id: string, name: string, m: number, d: number, at = new Date().toISOString()): Birthdays {
  return {...b, people: b.people.map(p => p.id === id ? {id, name: cleanName(name), m, d, at} : p)};
}
export function removeFriend(b: Birthdays, id: string, at = new Date().toISOString()): Birthdays {
  return {...b, people: b.people.map(p => p.id === id ? {id, name: '', m: 1, d: 1, at, del: true as const} : p)};
}
/** why an edit cannot be saved ('' = it can) */
export function friendProblem(b: Birthdays, name: string, m: unknown, d: unknown, editing = ''): string {
  if (!cleanName(name)) return name.trim().length > NAME_MAX ? `A name can have at most ${NAME_MAX} characters.` : 'Type a name first.';
  if (!validMD(m, d)) return 'Pick a month and a day.';
  if (!editing && live(b).length >= MAX_PEOPLE) return `The list is full (${MAX_PEOPLE} people).`;
  return '';
}

/* ---------- the day ---------- */
/** the day a birthday is celebrated in a year: Feb 29 on Feb 28 when that year has none */
export const celebratedOn = (m: number, d: number, year: number): [number, number] => m === 2 && d === 29 && !leap(year) ? [2, 28] : [m, d];
const hits = (m: number, d: number, date: string) => { const [cm, cd] = celebratedOn(m, d, +date.slice(0, 4)); return +date.slice(5, 7) === cm && +date.slice(8, 10) === cd; };
/** whose birthday is on this date ('YYYY-MM-DD', the person's own day in their zone): their own, and the names of family & friends */
export function birthdaysOn(date: string, b: Birthdays | null): {own: boolean; others: string[]} {
  if (!b || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return {own: false, others: []};
  const own = !!b.own && b.own.m !== null && b.own.d !== null && hits(b.own.m, b.own.d, date);
  return {own, others: live(b).filter(p => hits(p.m, p.d, date)).map(p => p.name)};
}
/** "Mom's", "James's" */
const poss = (n: string) => n + "'s";
/** a list in words: "Mom's", "Mom's and Sam's", "Mom's, Sam's and Ann's" */
const words = (xs: string[]) => xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1];
/** "🎈 Today is Mom's birthday" / "🎈 Today: Mom's and Sam's birthdays" ('' for nobody) */
export function othersText(names: string[]): string {
  if (!names.length) return '';
  return names.length === 1 ? `🎈 Today is ${poss(names[0])} birthday` : `🎈 Today: ${words(names.map(poss))} birthdays`;
}
/** the first name for the card: the first word of the person's name ('' if none) */
export const firstName = (full: string | null | undefined) => (full || '').trim().split(/\s+/)[0] || '';
export const ownText = (first: string) => first ? `Happy birthday, ${first}! 🎂` : 'Happy birthday! 🎂';
/** "Mar 14" (never with the year) */
export const mdLabel = (m: number, d: number) => `${MON3[m - 1]} ${d}`;

/* ---------- with the holidays (surprises.ts) ---------- */
/** what the logo shows: the person's own birthday takes the spot (🎂) over a holiday; family & friends' add a 🎈 next to
    the holiday's decoration (the logo's top corner), or take the spot on an ordinary day */
export function logoPlan(holidayDeco: boolean, bd: {own: boolean; others: string[]}): {spot: 'cake' | 'holiday' | 'balloon' | ''; extraBalloon: boolean} {
  const others = bd.others.length > 0;
  if (bd.own) return {spot: 'cake', extraBalloon: others};
  if (holidayDeco) return {spot: 'holiday', extraBalloon: others};
  return {spot: others ? 'balloon' : '', extraBalloon: false};
}
/** the message when the logo is tapped: the person's own birthday first, then the holiday's greeting, then family &
    friends' birthdays ('' = no message) */
export function tapMessage(own: boolean, first: string, holidayToast: string, others: string[]): string {
  return [own ? '🎂 ' + ownText(first).replace(/ 🎂$/, '') : '', holidayToast, othersText(others)].filter(Boolean).join(' · ');
}
